import * as mutations from 'src/graphql/mutations'
import {
  BlockingCreateMutation,
  BlockingUpdateMutation,
  OptimisticCreateMutation,
  OptimisticDeleteMutation,
  OptimisticUpdateMutation,
} from 'src/graphql/Mutation'
import { compact } from 'src/helpers'

// WRITE DECLARATIONS IN ALPHABETICAL ORDER

export const acceptCampaignShare = OptimisticDeleteMutation({
  operation: mutations.AcceptCampaignShare,
  resourceTypename: 'CampaignShare',
  resourceIds: ({ id }) => [id],
  failureMessage:
    'Sorry, could not accept campaign share, please try again later.',
})

export const acceptSharedContacts = OptimisticDeleteMutation({
  operation: mutations.AcceptSharedContact,
  resourceTypename: 'ContactShare',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Sorry, could not add contact, please try again later.',
})

export const cancelPendingCards = BlockingUpdateMutation({
  operation: mutations.CancelPendingCards,
  transitionMessage: 'Cancelling pending cards...',
  failureMessage:
    'Sorry, could not cancel pending cards, please try again later.',
})

export const cancelStripeSubscription = BlockingUpdateMutation({
  operation: mutations.CancelStripeSubscription,
  transitionMessage: 'Cancelling subscription...',
  failureMessage: 'Failed to cancel subscription.',
})

export const createAccount = BlockingUpdateMutation({
  operation: mutations.CreateAccount,
  transitionMessage: 'Creating account...',
  failureMessage: 'Failed to create account.',
})

export const createAffiliate = BlockingUpdateMutation({
  operation: mutations.CreateAffiliate,
  transitionMessage: 'Upgrading subscription...',
  failureMessage: 'Failed to upgrade subscription.',
})

export const createCard = BlockingCreateMutation({
  operation: mutations.CreateCard,
  transitionMessage: 'Crafting your perfect card...',
  failureMessage: 'Failed to create card.',
})

export const createCampaign = BlockingCreateMutation({
  operation: mutations.CreateCampaign,
  transitionMessage: 'Creating Campaign...',
  failureMessage: 'Failed to create campaign.',
})

export const createContact = OptimisticCreateMutation({
  operation: mutations.CreateOrUpdateContact,
  createdResources: ({ contact }) => [
    {
      __typename: 'Contact' as 'Contact',
      id: contact.id,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      company: contact.companyName ?? '',
      address1: contact.address1 ?? '',
      address2: contact.address2 ?? '',
      city: contact.city ?? '',
      state: contact.state ?? '',
      postalCode: contact.postalCode ?? '',
      country: contact.country ?? null,
      homePhone: contact.homePhone ?? null,
      cellPhone: contact.cellPhone ?? contact.cellNumber ?? null,
      workPhone: contact.workPhone ?? null,
      faxNumber: contact.faxNumber ?? null,
      emailAddress: contact.emailAddress ?? null,
      website:
        contact.website ??
        (contact.websites && contact.websites.length > 0
          ? contact.websites[0]
          : ''),
      notes: [],
      isPrimary: true,
      parent: contact.parent
        ? { __typename: 'Contact' as 'Contact', id: contact.parent }
        : null,
      spouse: contact.spouse
        ? { __typename: 'Contact', id: contact.spouse }
        : null,
      children: [],
      groups: contact.groups?.map(id => ({ __typename: 'Group', id })) ?? [],
      anniversary: contact.anniversary
        ? { ...contact.anniversary, year: contact.anniversary.year ?? null }
        : null,
      birthday: contact.birthday
        ? { ...contact.birthday, year: contact.birthday.year ?? null }
        : null,
    },
  ],
  failureMessage: 'Failed to create contact.',
})

export const createContacts = BlockingCreateMutation({
  operation: mutations.CreateContacts,
  transitionMessage: 'Creating Contacts...',
  failureMessage: 'Failed to create contacts.',
})

export const createGroup = BlockingCreateMutation({
  operation: mutations.CreateGroup,
  transitionMessage: 'Creating Group...',
  failureMessage: 'Failed to create group.',
})

export const createImageWithURL = BlockingCreateMutation({
  operation: mutations.CreateImageWithURL,
  transitionMessage: 'Creating Image...',
  failureMessage: 'Failed to create image.',
})

export const createOrder = BlockingUpdateMutation({
  operation: mutations.CreateOrder,
  transitionMessage: 'Creating Order...',
  failureMessage: 'Sorry, could not create order, please try again later',
})

export const createBackPanel = BlockingCreateMutation({
  operation: mutations.CreateOrUpdateBackPanel,
  transitionMessage: 'Saving Layout...',
  failureMessage: 'Failed to save layout.',
})

export const createOrUpdateLayout = BlockingUpdateMutation({
  operation: mutations.CreateOrUpdateBackPanel,
  transitionMessage: 'Saving Layout...',
  failureMessage: 'Failed to save layout.',
})

export const createStripeOrder = BlockingCreateMutation({
  operation: mutations.CreateStripeOrder,
  transitionMessage: 'Creating Order...',
  failureMessage: 'Sorry, could not create order, please try again later',
})

export const createUserLoginLink = BlockingCreateMutation({
  operation: mutations.CreateUserLoginLink,
  transitionMessage: 'Redirecting...',
  failureMessage: 'Failed to redirect.',
})

export const deleteCampaign = OptimisticDeleteMutation({
  operation: mutations.DeleteCampaign,
  resourceTypename: 'Campaign',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete campaign.',
})

export const deleteCampaignShare = OptimisticDeleteMutation({
  operation: mutations.DeleteCampaignShare,
  resourceTypename: 'CampaignShare',
  resourceIds: ({ id }) => [id],
  failureMessage:
    'Sorry, could not remove campaign share, please try again later.',
})

export const deleteCards = OptimisticDeleteMutation({
  operation: mutations.BulkDeleteCards,
  resourceTypename: 'Card',
  resourceIds: ({ cards }) => cards,
  failureMessage: 'Failed to delete cards.',
})

export const deleteContact = OptimisticDeleteMutation({
  operation: mutations.DeleteContact,
  resourceTypename: 'Contact',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete contact.',
})

export const deleteContactShare = OptimisticDeleteMutation({
  operation: mutations.DeleteContactShare,
  resourceTypename: 'ContactShare',
  resourceIds: ({ id }) => [id],
  failureMessage:
    'Sorry, could not remove contact share, please try again later.',
})

export const deleteContacts = OptimisticDeleteMutation({
  operation: mutations.BulkDeleteContacts,
  resourceTypename: 'Contact',
  resourceIds: ({ contacts }) => contacts,
  failureMessage: 'Failed to delete contacts.',
})

export const deleteGroup = OptimisticDeleteMutation({
  operation: mutations.DeleteGroup,
  resourceTypename: 'Group',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete group.',
})

export const deleteImage = OptimisticDeleteMutation({
  operation: mutations.DeleteImage,
  resourceTypename: 'Image',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete image.',
})

export const deleteLayout = OptimisticDeleteMutation({
  operation: mutations.DeletePanelLayout,
  resourceTypename: 'Layout',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete layout.',
})

export const deleteOrder = OptimisticDeleteMutation({
  operation: mutations.DeleteOrder,
  resourceTypename: 'Order',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Failed to delete order.',
})

export const deleteOrders = OptimisticDeleteMutation({
  operation: mutations.BulkDeleteOrders,
  resourceTypename: 'Order',
  resourceIds: ({ orders }) => orders,
  failureMessage: 'Failed to delete orders.',
})

export const deleteReminder = OptimisticDeleteMutation({
  operation: mutations.DeleteReminder,
  resourceTypename: 'Reminder',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Sorry, could not remove reminder, please try again later.',
})

export const denyNotification = OptimisticDeleteMutation({
  operation: mutations.DenyNotification,
  resourceTypename: 'Notification',
  resourceIds: ({ id }) => [id],
  failureMessage: 'Sorry, could not deny notification, please try again later.',
})

export const deleteSendableCards = OptimisticDeleteMutation({
  operation: mutations.BulkDeleteSendableCards,
  resourceTypename: 'SendableCard',
  resourceIds: ({ cards }) => cards,
  failureMessage: 'Failed to delete cards.',
})

export const favoriteCard = BlockingCreateMutation({
  operation: mutations.FavoriteCard,
  transitionMessage: 'Favoriting card...',
  failureMessage: 'Failed to favorite card.',
})

export const getOrCreateEditableCardForRecipient = BlockingCreateMutation({
  operation: mutations.GetOrCreateEditableCardForRecipient,
  transitionMessage: 'Loading card...',
  failureMessage: 'Failed to load card.',
})

export const loginUser = BlockingUpdateMutation({
  operation: mutations.LoginUser,
  transitionMessage: 'Logging in...',
  failureMessage: 'Failed to log in',
})

export const purchaseProduct = BlockingUpdateMutation({
  operation: mutations.PurchaseProduct,
  transitionMessage: 'Purchasing Product...',
  failureMessage: 'Failed to purchase product.',
})

export const requestPhoneNumberVerification = BlockingCreateMutation({
  operation: mutations.RequestPhoneNumberVerification,
  transitionMessage: 'Requesting Phone Number Verification...',
  failureMessage: 'Failed to request phone number verification.',
})

export const sendPausedRecipients = BlockingUpdateMutation({
  operation: mutations.SendPausedRecipients,
  transitionMessage: 'Sending paused recipients',
  failureMessage: 'Failed to send paused recipients',
})

export const shareCampaign = BlockingCreateMutation({
  operation: mutations.ShareCampaign,
  transitionMessage: 'Sharing campaign...',
  failureMessage: 'Failed to share campaign.',
})

export const shareContact = BlockingCreateMutation({
  operation: mutations.ShareContact,
  transitionMessage: 'Sharing contact...',
  failureMessage: 'Failed to share contact.',
})

export const sendMagicLink = BlockingCreateMutation({
  operation: mutations.SendMagicLink,
  transitionMessage: 'Sending Magic Link...',
  failureMessage: 'Failed to send magic link.',
})

export const sendOrder = BlockingCreateMutation({
  operation: mutations.SendOrder,
  transitionMessage: 'Sending Order...',
  failureMessage: 'Failed to send order.',
})

export const updateAccount = BlockingUpdateMutation({
  operation: mutations.UpdateAccount,
  transitionMessage: 'Updating Account...',
  failureMessage: 'Failed to update account.',
})

export const updateCampaign = BlockingUpdateMutation({
  operation: mutations.UpdateCampaign,
  transitionMessage: 'Saving campaign...',
  failureMessage: 'Failed to save campagin.',
})

export const updateCard = BlockingUpdateMutation({
  operation: mutations.UpdateCard,
  transitionMessage: 'Saving your card...',
  failureMessage: 'Failed to save card.',
})

export const updateContact = OptimisticUpdateMutation({
  operation: mutations.CreateOrUpdateContact,
  updatedResources: ({ contact }) =>
    compact({
      __typename: 'Contact',
      ...contact,
      company: contact.companyName,
      parent:
        contact.parent !== undefined && contact.parent !== null
          ? { __typename: 'Contact' as 'Contact', id: contact.parent }
          : contact.parent,
      spouse:
        contact.spouse !== undefined && contact.spouse !== null
          ? { __typename: 'Contact' as 'Contact', id: contact.spouse }
          : contact.spouse,
      anniversary:
        contact.anniversary !== undefined && contact.anniversary !== null
          ? { ...contact.anniversary, year: contact.anniversary.year || null }
          : contact.anniversary,
      birthday:
        contact.birthday !== undefined && contact.birthday !== null
          ? { ...contact.birthday, year: contact.birthday.year || null }
          : contact.birthday,
      groups: contact.groups?.map(id => ({ __typename: 'Group', id })),
      notes: contact.notes?.map(note => ({
        ...note,
        date: note.date || null,
      })),
    }),
  failureMessage: 'Failed to update contact.',
})

export const updateOrder = BlockingUpdateMutation({
  operation: mutations.UpdateOrder,
  transitionMessage: 'Saving order...',
  failureMessage: 'Failed to save order.',
})

export const updateProductionInfo = BlockingUpdateMutation({
  operation: mutations.UpdateProductionInfo,
  transitionMessage: 'Updating production info...',
  failureMessage: 'Failed to update production info.',
})

export const updateRecipient = BlockingUpdateMutation({
  operation: mutations.UpdateRecipient,
  transitionMessage: 'Updating recipient...',
  failureMessage: 'Failed to update recipient.',
})

export const updateStorefrontMugshot = BlockingCreateMutation<
  mutations.UpdateStorefrontMugshotData,
  { image_file: File; tags: string[] }
>({
  operation: variables => ({
    ...mutations.UpdateStorefrontMugshot(),
    variables,
  }),
  transitionMessage: 'Uploading Image...',
  failureMessage: 'Failed to upload image.',
  isFormData: true,
})

export const uploadImage = BlockingCreateMutation<
  mutations.UploadImageData,
  { image_file: File; tags: string[] }
>({
  operation: variables => ({
    ...mutations.UploadImage(),
    variables,
  }),
  transitionMessage: 'Uploading Image...',
  failureMessage: 'Failed to upload image.',
  isFormData: true,
})

export const unfavoriteCard = BlockingCreateMutation({
  operation: mutations.UnfavoriteCard,
  transitionMessage: 'Unfavoriting card...',
  failureMessage: 'Failed to unfavorite card.',
})

export const updateGroup = OptimisticUpdateMutation({
  operation: mutations.UpdateGroup,
  updatedResources: ({ group }) => [
    {
      __typename: 'Group',
      ...group,
      members: group.members?.concat(group.newMembers ?? [])?.map(id => ({
        id,
        __typename: 'Contact',
      })),
    },
  ],
  failureMessage: 'Failed to update group.',
})

export const updateUserLabelOverrides = BlockingCreateMutation({
  operation: mutations.UpdateUserLabelOverrides,
  transitionMessage: 'Updating account preferences...',
  failureMessage: 'Failed to update user labels.',
})

export const validateUsername = BlockingCreateMutation({
  operation: mutations.ValidateUsername,
  transitionMessage: 'Validating Username...',
  failureMessage: 'Failed to validate username.',
})

export const validatePhoneNumber = BlockingCreateMutation({
  operation: mutations.ValidatePhoneNumber,
  transitionMessage: 'Validating Phone Number...',
  failureMessage: 'Failed to validate phone number.',
})

export const verifyPhoneNumber = BlockingCreateMutation({
  operation: mutations.VerifyPhoneNumber,
  transitionMessage: 'Verifying Code...',
  failureMessage: 'Failed to verify code.',
})

// WRITE DECLARATIONS IN ALPHABETICAL ORDER
